import { t } from "@lingui/macro";
import { invariantResponse } from "@winamp/invariant";
import { redirectWithSuccess } from "@winamp/toast/index.js";
import { parseWithValibot } from "conform-to-valibot";
import { useLoaderData } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import * as v from "valibot";
import { DetailsPageLayout } from "~/components/layout/DetailsPageLayout";
import { canCreateService, canSubmitService } from "~/models/service-issue";
import { requireAuth } from "~/modules/auth/auth.server";
import { getListboxOptions } from "~/modules/options/get-listbox-options";
import { $contextPath } from "~/modules/routing/context-path.server";
import { useContextPath } from "~/modules/routing/use-context-path";
import { OpenAPI, getResponseErrors } from "~/modules/sdk/client.server";
import { handleMetadataWarningDialog } from "~/utils/handleMetadataWarningDialog";
import { ReleaseCard } from "./components/ReleaseCard";
import { ReleaseForm } from "./components/ReleaseForm";
import {
  getNewReleaseSchema,
  useReleaseForm,
} from "./form/use-edit-release-form";

export const getDistributionCreateSchema = () =>
  v.object({
    audioOrCollectionUid: v.string(),
    type: v.optional(v.picklist(["audio", "collection"]), "audio"),
  });

type NewReleaseCandidateQuery = v.InferOutput<
  ReturnType<typeof getDistributionCreateSchema>
>;
export type SearchParams = NewReleaseCandidateQuery;

export async function action({ request, params }: ActionFunctionArgs) {
  const { accessToken } = await requireAuth(request);

  invariantResponse(
    params.artistId && params.artistId !== "all",
    "An artist must be selected.",
  );

  const formData = await request.formData();

  await handleMetadataWarningDialog(formData, request, accessToken);

  const url = new URL(request.url);
  const searchParams = url.searchParams;

  formData.append("type", searchParams.get("type") ?? "");
  formData.append(
    "audioOrCollectionUid",
    searchParams.get("audioOrCollectionUid") ?? "",
  );

  const submission = parseWithValibot(formData, {
    schema: v.intersect([getDistributionCreateSchema(), getNewReleaseSchema()]),
  });

  if (submission.status !== "success") {
    return submission.reply({
      resetForm: false,
      formErrors: ["The release could not be created."],
    });
  }

  const body = {
    submit: submission.value.action === "submit",
    collectionUid:
      submission.value.type === "collection"
        ? submission.value.audioOrCollectionUid
        : null,
    audioUid:
      submission.value.type === "audio"
        ? submission.value.audioOrCollectionUid
        : null,
    preferredReleaseDate:
      submission.value.showCustomReleaseDate === "off"
        ? (submission.value.preferredReleaseDate ?? null)
        : null,
    originalReleaseDate:
      submission.value.showReReleaseFields === "on"
        ? (submission.value.originalReleaseDate ?? null)
        : null,
    trackPriceCategoryId: submission.value.trackPriceCategoryId
      ? Number(submission.value.trackPriceCategoryId)
      : null,
    albumPriceCategoryId: submission.value.albumPriceCategoryId
      ? Number(submission.value.albumPriceCategoryId)
      : null,
    excludedCountryIds: [],
    dspIds: [],
    ean:
      submission.value.showReReleaseFields === "on"
        ? (submission.value.ean ?? null)
        : null,
  };

  const { error } = await OpenAPI.POST("/api/artists/{artistUid}/releases", {
    params: {
      path: { artistUid: params.artistId },
    },
    body: body,
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error) {
    const { fieldErrors, formErrors } = getResponseErrors(error);

    return submission.reply({
      resetForm: false,
      fieldErrors,
      formErrors: fieldErrors
        ? formErrors
        : (formErrors ?? [t`Creating the release did not work. Try Again`]),
    });
  }

  return redirectWithSuccess(
    await $contextPath(request, params, "/distribution"),
    {
      title: t({ message: "Release created!" }),
      message: t({ message: "The release has been created with success." }),
    },
  );
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { accessToken } = await requireAuth(request, { throwOnRefresh: true });

  invariantResponse(
    params.artistId && params.artistId !== "all",
    "An artist must be selected.",
  );

  const url = new URL(request.url);

  const newReleaseCandidateQuery = v.parse(
    getDistributionCreateSchema(),
    Object.fromEntries(url.searchParams),
  );

  const [userPreferences, releaseCandidate, artist, listboxOptions, dsps] =
    await Promise.all([
      OpenAPI.GET("/api/user/preferences", {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
      getReleaseCandidate(params.artistId, newReleaseCandidateQuery),
      OpenAPI.GET("/admin/artists/{artistUid}", {
        params: { path: { artistUid: params.artistId } },
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
      getListboxOptions(accessToken, [
        "individualTrackPricing",
        "albumTrackPricing",
      ]),
      OpenAPI.GET("/api/digital-service-providers", {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { query: { musicDistributionPlatformId: 10 } }, // hardcoded Sonosuite value for now
      }),
    ]);

  invariantResponse(artist.data, t`Failed to fetch artist ${params.artistId}`, {
    status: 500,
  });

  return {
    artist: artist.data.data,
    releaseCandidate,
    dsps,
    userPreferences: userPreferences.data?.data,
    ...listboxOptions,
  };

  async function getReleaseCandidate(
    artistUid: string,
    { type, ...releaseCandidateQuery }: NewReleaseCandidateQuery,
  ) {
    const { data, error, response } = await OpenAPI.GET(
      "/api/artists/{artistUid}/release-candidate",
      {
        params: {
          path: { artistUid: artistUid },
          query: {
            audioUid:
              type === "audio"
                ? releaseCandidateQuery.audioOrCollectionUid
                : null,
            collectionUid:
              type === "collection"
                ? releaseCandidateQuery.audioOrCollectionUid
                : null,
          },
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );

    if (error) {
      throw new Response(JSON.stringify(error), { status: response.status });
    }

    return data.data;
  }
}

export default function Page() {
  const { artist, releaseCandidate, dsps } = useLoaderData<typeof loader>();

  const contextPath = useContextPath();

  const [form, fields] = useReleaseForm(releaseCandidate);

  return (
    <DetailsPageLayout
      breadcrumbs={[
        {
          label: t`Distribution`,
          to: contextPath("/distribution"),
          key: "distribution",
        },
        {
          label: releaseCandidate.title,
          truncate: true,
          key: "release",
        },
      ]}
    >
      <div className="flex-shrink-0 w-full md:w-[40%] md:max-w-[352px]">
        <ReleaseCard releaseOrRC={releaseCandidate} artist={artist} />
      </div>
      <div className="flex-grow w-full md:w-1/2 lg:w-2/3">
        <ReleaseForm
          canSubmit={
            !!releaseCandidate.coverImagePath &&
            canSubmitService(releaseCandidate.entries) &&
            canCreateService(releaseCandidate.entries)
          }
          form={form}
          fields={fields}
          release={releaseCandidate}
          allDsps={dsps?.data?.data.items}
        />
      </div>
    </DetailsPageLayout>
  );
}
